import React, { useState } from 'react';
import { colors, FONT, radii } from '../../../theme';
import { S } from '../dashboardStyles';
import { SettingsCtx } from '../settings/SettingsContext';
import SettingsRoot from '../settings/SettingsRoot';
import SettingsScreen from '../settings/SettingsScreen';
import {
  ProfileScreen, CallsScreen, RouteScreen, LoginScreen,
  BackgroundScreen, TrustScreen, ArchiveScreen, AboutScreen,
} from '../settings/screens';
import ServersScreen from '../settings/ServersScreen';
import { ConfirmSheet } from '../Sheets';
import AdminUsersScreen from '../settings/admin/AdminUsersScreen';
import AdminUserCard from '../settings/admin/AdminUserCard';
import AdminAuditScreen from '../settings/admin/AdminAuditScreen';

/**
 * Settings, as a list of screens rather than one scroll.
 *
 * It was eleven blocks in a row, in the order they happened to be written,
 * with the two heaviest — the security card and the archive — at the bottom
 * and always mounted. Anchors would fix the first visit and not the second:
 * scrolling is not a state, and there is no way to leave it.
 *
 * This file is now the router. It takes the same props it always did and puts
 * them in a context instead of threading thirty-five of them through seven
 * screens — the list would have grown with every screen rather than shrunk.
 */
export default function SettingsTab(props) {
  const {
    user, logout, adminUsers, adminError, createUser, deleteUser,
    createdCreds, credsCopied, copyCredentials, shareCredentials, setCreatedCreds, setCredsCopied,
  } = props;

  /**
   * Which screen is open lives in the navigation stack, not in this component.
   *
   * That is what gives every screen an address: hardware Back and the iOS edge
   * swipe walk out of them the way they walk out of a chat, and a banner about
   * a failed connection can send somebody straight to
   * settings/calls/route — which is exactly the moment that setting is worth
   * finding.
   */
  const { nav } = props;
  const top = nav?.top?.name || '';
  const screen = top.startsWith('settings/') ? top.slice('settings/'.length) : null;
  const open = (id) => nav?.push(`settings/${id}`);
  const back = () => nav?.pop();

  const [adminUserId, setAdminUserId] = useState(null);
  const [confirmLogout, setConfirmLogout] = useState(false);

  // The archive is the one thing on this screen whose absence costs a
  // conversation. The hint appears only while it is true.
  let archiveDate = null;
  try { archiveDate = localStorage.getItem('voxiox_archive_last_at'); } catch (e) { /* private mode */ }

  const ctx = {
    ...props,
    archiveDate: archiveDate ? new Date(archiveDate).toLocaleDateString('ru-RU') : null,
    archiveNeeded: !archiveDate,
  };

  const body = screen === 'profile' ? <ProfileScreen onBack={back} />
    : screen === 'calls' ? <CallsScreen onBack={back} onOpenRoute={() => open('route')} />
    : screen === 'route' ? <RouteScreen onBack={back} />
    : screen === 'login' ? <LoginScreen onBack={back} />
    : screen === 'background' ? <BackgroundScreen onBack={back} />
    : screen === 'trust' ? <TrustScreen onBack={back} />
    : screen === 'archive' ? <ArchiveScreen onBack={back} />
    : screen === 'about' ? <AboutScreen onBack={back} />
    : screen === 'servers' ? <ServersScreen onBack={back} />
    : screen === 'admin-user' ? (
      <AdminUserCard userId={adminUserId} currentUserId={user?.id}
        onBack={() => { setAdminUserId(null); back(); }}
        onDeleted={() => { setAdminUserId(null); back(); }} />
    )
    : screen === 'admin-audit' ? <AdminAuditScreen onBack={back} />
    : screen === 'admin' ? (
      <AdminUsersScreen onBack={back} currentUserId={user?.id}
        onOpenUser={(id) => { setAdminUserId(id); open('admin-user'); }} />
    )
    : <SettingsRoot onOpen={open} onLogout={() => setConfirmLogout(true)} />;

  return (
    <SettingsCtx.Provider value={ctx}>
      {body}
      {confirmLogout && (
        <ConfirmSheet
          title="Выйти из аккаунта?"
          consequences={[
            'Переписка останется на этом устройстве, но откроется только после входа',
            'Звонки и сообщения перестанут приходить, пока вы не войдёте снова',
            'Понадобится логин и пароль — тот, что выдавался при регистрации',
          ]}
          confirmLabel="Выйти"
          onConfirm={() => { setConfirmLogout(false); logout(); }}
          onClose={() => setConfirmLogout(false)}
        />
      )}
    </SettingsCtx.Provider>
  );
}
